import type { TenantPublicTheme } from '@/common/api/models/tenantPublicTheme';
import {
  darkenColor,
  desaturateColor,
  getContrastRatio,
  getLuminance,
  hexToRgb,
  hexToRgbTuple,
  hslToRgb,
  lightenColor,
  mixColors,
  rgbToHex,
  rgbToHsl,
  rotateHue,
  setHslLightness,
  setHslSaturation,
} from '@/common/theme/colorMath';

/** Fixed cool neutral anchor — not a tenant token. */
export const ORB_COOL_ANCHOR = '#1A2B3D';
export const ORB_BG_CARD = '#FFFFFF';
export const ORB_INTELLIGENCE_TEAL = '#2A6F7A';
export const ORB_INTELLIGENCE_INDIGO = '#4C5FD5';

export type OrbPaletteInput = Pick<TenantPublicTheme, 'primaryColor' | 'secondaryColor' | 'accentColor'>;

export type OrbPaletteStops = {
  well: string;
  cloudA: string;
  cloudB: string;
  cloudC: string;
  core: string;
  rim: string;
  haloRgb: [number, number, number];
};

export type OrbPaletteGuards = {
  emergenceGap: number;
  cloudSaturation: number;
  wellSaturation: number;
  rimContrast: number;
  rimContrastTarget: string;
};

const clampSaturation = (hex: string, max: number) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  return setHslSaturation(hex, Math.min(hsl.s, max));
};

const cloudFromBrand = (hex: string) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  const next = hslToRgb(hsl.h, Math.min(hsl.s, 71.5), 70);
  return rgbToHex(next.r, next.g, next.b);
};

const wellFromBrand = (brandHex: string, secondaryHex: string) => {
  const brandDeep = desaturateColor(darkenColor(brandHex, 0.55), 0.45);
  const secondaryDeep = desaturateColor(darkenColor(secondaryHex, 0.5), 0.5);
  const tinted = mixColors(brandDeep, secondaryDeep, 0.35);
  const neutralWell = mixColors(tinted, ORB_COOL_ANCHOR, 0.72);
  return clampSaturation(neutralWell, 40);
};

const huesAreClose = (a: string, b: string, threshold = 18) => {
  const rgbA = hexToRgb(a);
  const rgbB = hexToRgb(b);
  if (!rgbA || !rgbB) return false;
  const hslA = rgbToHsl(rgbA.r, rgbA.g, rgbA.b);
  const hslB = rgbToHsl(rgbB.r, rgbB.g, rgbB.b);
  const diff = Math.abs(((hslA.h - hslB.h + 540) % 360) - 180);
  return diff < threshold;
};

const isNearWhiteBrand = (hex: string) => getLuminance(hex) > 0.7;
const isNearBlackBrand = (hex: string) => getLuminance(hex) < 0.06;
const isAchromaticBrand = (hex: string) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  return hsl.s < 12;
};

export function getOrbPaletteGuards(palette: OrbPaletteStops): OrbPaletteGuards {
  const cloudL = Math.max(
    getLuminance(palette.cloudA),
    getLuminance(palette.cloudB),
    getLuminance(palette.cloudC),
  );
  const wellL = getLuminance(palette.well);

  const cloudSaturations = [palette.cloudA, palette.cloudB, palette.cloudC].map((hex) => {
    const rgb = hexToRgb(hex);
    return rgb ? rgbToHsl(rgb.r, rgb.g, rgb.b).s : 0;
  });
  const wellRgb = hexToRgb(palette.well);
  const wellHsl = wellRgb ? rgbToHsl(wellRgb.r, wellRgb.g, wellRgb.b) : { s: 0 };

  return {
    emergenceGap: cloudL - wellL,
    cloudSaturation: Math.max(...cloudSaturations),
    wellSaturation: wellHsl.s,
    rimContrast: getContrastRatio(palette.rim, ORB_BG_CARD),
    rimContrastTarget: ORB_BG_CARD,
  };
}

/**
 * Resolve Deep-Field Nebula artwork stops once per tenant/theme version.
 * Colours are decorative identity data — not reusable UI tokens.
 */
function enforceEmergenceGap(palette: OrbPaletteStops): OrbPaletteStops {
  let next = palette;

  for (let attempt = 0; attempt < 4; attempt += 1) {
    const guards = getOrbPaletteGuards(next);
    if (guards.emergenceGap >= 0.45) return next;

    const wellL = getLuminance(next.well);
    const targetCloudL = wellL + 0.46;
    const lift = (hex: string) => {
      const current = getLuminance(hex);
      if (current >= targetCloudL) return hex;
      const rgb = hexToRgb(hex);
      if (!rgb) return hex;
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      return setHslLightness(hex, Math.min(84, hsl.l + (targetCloudL - current) * 130));
    };

    const wellRgb = hexToRgb(next.well);
    const wellHsl = wellRgb ? rgbToHsl(wellRgb.r, wellRgb.g, wellRgb.b) : { l: 12 };

    next = {
      ...next,
      cloudA: lift(next.cloudA),
      cloudB: lift(next.cloudB),
      cloudC: lift(next.cloudC),
      core: lift(next.core),
      well: setHslLightness(next.well, Math.max(6, wellHsl.l - 3)),
    };
  }

  return next;
}

export function deriveOrbPalette(input: OrbPaletteInput): OrbPaletteStops {
  const brand = input.primaryColor;
  const secondary = input.secondaryColor || ORB_INTELLIGENCE_TEAL;

  let cloudA: string;
  let cloudB: string;
  let well: string;
  let haloSource = brand;

  if (isAchromaticBrand(brand)) {
    cloudA = cloudFromBrand(ORB_INTELLIGENCE_TEAL);
    cloudB = cloudFromBrand(ORB_INTELLIGENCE_INDIGO);
    well = wellFromBrand(ORB_COOL_ANCHOR, ORB_COOL_ANCHOR);
    haloSource = ORB_INTELLIGENCE_TEAL;
  } else if (isNearBlackBrand(brand)) {
    well = clampSaturation(desaturateColor(darkenColor(brand, 0.15), 0.2), 40);
    cloudA = cloudFromBrand(lightenColor(brand, 0.35));
    cloudB = cloudFromBrand(lightenColor(secondary, 0.45));
    haloSource = lightenColor(brand, 0.2);
  } else if (isNearWhiteBrand(brand)) {
    const midBrand = setHslLightness(darkenColor(brand, 0.25), 52);
    cloudA = cloudFromBrand(midBrand);
    cloudB = cloudFromBrand(ORB_INTELLIGENCE_TEAL);
    well = wellFromBrand(ORB_COOL_ANCHOR, ORB_COOL_ANCHOR);
    haloSource = midBrand;
  } else {
    cloudA = cloudFromBrand(brand);
    cloudB = cloudFromBrand(secondary);
    well = wellFromBrand(brand, secondary);
    haloSource = brand;
  }

  if (huesAreClose(cloudA, cloudB)) {
    cloudB = cloudFromBrand(rotateHue(secondary, 30));
  }

  const cloudC = mixColors(cloudA, cloudB, 0.5);
  const core = lightenColor(mixColors(cloudA, '#FFF8F0', 0.65), 0.08);
  const rim = ORB_COOL_ANCHOR;

  return enforceEmergenceGap({
    well,
    cloudA,
    cloudB,
    cloudC,
    core,
    rim,
    haloRgb: hexToRgbTuple(haloSource),
  });
}
